import './TransactionDetailsDialog.scss';
import { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { ReactComponent as CloseIcon } from '../../assets/images/close-icon.svg';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import { TransactionService } from '../../service/TransactionService';
import { MessageContext } from '../../context/MessageContext';
import { RenderedHelper } from '../../utils/render/RenderedHelper';
import { FormattedUtils } from '../../utils/FormattedUtils';
import moment from 'moment';
import LoadingOverlay from '../../component/loadingoverlay/LoadingOverlay';
import { AccessLevel, useAuth } from '../../hook/useAuth';

const TransactionDetailsDialog = ({ visible, transactionId, onHide, ...rest }) => {
    const { t } = useTranslation();
    const { showErrorMessage } = useContext(MessageContext);
    const isMounted = useRef(true);
    const [isLoading, setIsLoading] = useState(false);
    const [transaction, setTransaction] = useState(null);
    const { getAccessLevel } = useAuth();

    const accessLevel = useMemo(() => getAccessLevel(), [getAccessLevel]);

    useEffect(() => {
        if (transactionId && visible) {
            setTransaction(null);
            fetchTransaction();
        }
    }, [transactionId, visible]);

    const fetchTransaction = async () => {
        try {
            setIsLoading(true);
            const transaction = await TransactionService.getDetails(transactionId);
            if (isMounted.current) {
                setTransaction(transaction);
            }
        } catch (error) {
            showErrorMessage(t('transactionDetailsDialog.error.fetch'));
        } finally {
            if (isMounted.current) {
                setIsLoading(false);
            }
        }
    }

    const getHeader = () => {
        return (
            <div className="transaction-details-dialog-header">
                <span>{t('transactionDetailsDialog.header')}</span>
            </div>
        );
    }

    const renderDetailRow = (label, value) => (
        <div className="detail-row">
            <span className="detail-label">{label}:</span>
            <span className="detail-value">{value}</span>
        </div>
    );


    const showReasonCode = transaction?.reasonCode != null && (
        transaction?.payment?.paymentStatus === 'DECLINED' 
        || transaction?.payment?.paymentStatus === 'CANCELLED'
        || transaction?.payment?.paymentStatus === 'REFUNDED'
    );


    return (
        <Dialog {...rest}
            visible={visible}
            className='transaction-details-dialog'
            closable={true}
            modal
            focusOnShow={false}
            closeIcon={<CloseIcon />}
            header={getHeader()}
            onHide={onHide}
            footer={
                <div className="dialog-footer">
                    <Button
                        label={t('common.ok')}
                        onClick={onHide}
                        className="ok-button"
                        disabled={isLoading}
                    />
                </div>
            }
        >
            <LoadingOverlay loading={isLoading} >
            <div className="transaction-details-dialog-content">
                <div className="transaction-details-content">
                    {renderDetailRow(t('transactionDetailsDialog.transactionId'), transaction?.transactionId)}
                    {renderDetailRow(t('transactionDetailsDialog.dateTime'), RenderedHelper.formatTimestamp(transaction?.date))}
                    {renderDetailRow(t('transactionDetailsDialog.location'), transaction?.locationName)}
                    {renderDetailRow(t('transactionDetailsDialog.device'), transaction?.deviceName)}
                    {renderDetailRow(t('transactionDetailsDialog.product'), transaction?.products?.map(p => p.productName).join(', '))}
                    {renderDetailRow(t('transactionDetailsDialog.value'), transaction ? FormattedUtils.formatPrice(transaction?.totalGrossAmount, transaction?.currency) : '')}
                    {renderDetailRow(t('transactionDetailsDialog.tid'), transaction?.tid)}
                    {renderDetailRow(t('transactionDetailsDialog.payment'), RenderedHelper.formatEnum(transaction?.payment?.paymentMethod, 'PaymentMethod'))}
                    {renderDetailRow(t('transactionDetailsDialog.paymentStatus'), RenderedHelper.formatEnum(transaction?.payment?.paymentStatus, 'PaymentStatus'))}
                    {showReasonCode && renderDetailRow(t('transactionDetailsDialog.reasonCode'), transaction?.reasonCode)}
                    {renderDetailRow(t('transactionDetailsDialog.authorisationNo'), transaction?.payment?.authorizationCode || '-')}
                    {renderDetailRow(t('transactionDetailsDialog.card'), transaction?.payment?.cardNumber)}


                    
                    {accessLevel === AccessLevel.ADMIN && renderDetailRow(t('transactionDetailsDialog.partnerName'), transaction?.operator?.name)}
                    {(accessLevel === AccessLevel.ADMIN || accessLevel === AccessLevel.OPERATOR) && renderDetailRow(t('transactionDetailsDialog.merchantName'), transaction?.merchant?.name)}

                </div>
            </div>
            </LoadingOverlay>
        </Dialog>
    )
}

export default TransactionDetailsDialog;